import { Grid, AutocompleteRenderInputParams } from '@mui/material';
import { Button } from '@components/button';
import { SyntheticEvent, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { TextField } from '@components/textField';
import { Search } from '@assets/svgs';
import { EnteredAddress } from '@models/validation';
import { useAddressSuggestions } from '@services/smarty';
import { TypographyHighlighter } from '@components/typography/TypographyHighlighter';
import { Autocomplete } from '@components/autocomplete';
import { useQuote } from '@services/quotes';
import { useGetCustomer } from '@services/customer';
import { QuoteLocationAddress } from '@models/customer';
import { formatAddress, initialAddressState } from '@utils/address';
import { DEBOUNCE_DELAYS } from '@constants/debounceDelays';

export enum MailingCodeName {
  MailingCity = 'MailingCity',
  MailingState = 'MailingState',
  MailingStreet = 'MailingStreet',
  MailingStreet2 = 'MailingStreet2',
  MailingZip = 'MailingZip',
}

export type MailingAddressType = {
  MailingCity: string;
  MailingState: string;
  MailingStreet: string;
  MailingStreet2: string;
  MailingZip: string;
};

interface IMailingAddress {
  quoteId: string;
  primaryAddress: QuoteLocationAddress;
  selectMailingAddress: (address: MailingAddressType) => void;
}

export const OpenMailingAddress = ({ quoteId, primaryAddress, selectMailingAddress }: IMailingAddress) => {
  const [selectedValue, setSelectedValue] = useState<EnteredAddress | null>(initialAddressState);
  const [inputValue, setInputValue] = useState<string>('');

  const { addressSuggestions } = useAddressSuggestions(inputValue);
  const { quote } = useQuote(quoteId);
  const customerId: string = quote?.customer.id || '';
  const { customer } = useGetCustomer(customerId);
  const businessAddress = customer?.businessAddress;

  const debouncedInputValue = useDebouncedCallback((value) => {
    setInputValue(value);
  }, DEBOUNCE_DELAYS.TYPING);

  const handleChange = (event: SyntheticEvent, value: EnteredAddress | null) => {
    setSelectedValue(value);
    if (!value) return;
    selectMailingAddress({
      [MailingCodeName.MailingCity]: value.city,
      [MailingCodeName.MailingState]: value.state,
      [MailingCodeName.MailingStreet]: value.street1,
      [MailingCodeName.MailingStreet2]: '',
      [MailingCodeName.MailingZip]: value.zipCode,
    });
  };

  const handleInputChange = (event: SyntheticEvent, value: string) => {
    debouncedInputValue(value);
  };

  return (
    <Grid
      container
      size={{ xs: 12 }}
      sx={{
        flexDirection: 'row',
        p: 2,
      }}
    >
      <Grid
        size={{ xs: 12, sm: 12, md: 8, lg: 6 }}
        sx={{
          display: 'flex',
          justifyContent: 'space-around',
          padding: '16px 16px 16px 0',
          '@media(min-width: 560px)': { justifyContent: 'flex-start', padding: '16px 0 16px 0' },
          '@media(min-width: 900px)': { justifyContent: 'space-around', padding: '16px 8px 0 0' },
          '@media(min-width: 1536px)': { padding: '16px 16px 0 0' },
        }}
      >
        <Button
          variant='outlined'
          onClick={() =>
            selectMailingAddress({
              [MailingCodeName.MailingCity]: primaryAddress.city || '',
              [MailingCodeName.MailingState]: primaryAddress.jurisdiction.abbreviation || '',
              [MailingCodeName.MailingStreet]: primaryAddress.street1 || '',
              [MailingCodeName.MailingStreet2]: primaryAddress.street2 || '',
              [MailingCodeName.MailingZip]: primaryAddress.zipCode || '',
            })
          }
          sx={{
            marginRight: '30px',
            '@media(min-width: 900px)': { marginRight: '0' },
          }}
        >
          Use Primary Location
        </Button>
        <Button
          disabled={businessAddress === null || businessAddress === undefined}
          variant='outlined'
          onClick={() =>
            selectMailingAddress({
              [MailingCodeName.MailingCity]: businessAddress?.city || '',
              [MailingCodeName.MailingState]: businessAddress?.state || '',
              [MailingCodeName.MailingStreet]: businessAddress?.street1 || '',
              [MailingCodeName.MailingStreet2]: businessAddress?.street2 || '',
              [MailingCodeName.MailingZip]: businessAddress?.zipCode || '',
            })
          }
          sx={{ width: '176px' }}
        >
          Use Business Address
        </Button>
      </Grid>
      <Grid
        size={{ xs: 12, sm: 12, md: 4, lg: 3 }}
        sx={{
          padding: '16px 8px 0 0',
          '@media(min-width: 900px)': { padding: '16px 0 0 16px' },
          '@media(min-width: 1200px)': { padding: '16px 8px 0 16px' },
          '@media(min-width: 1536px)': { padding: '16px 8px 0 16px' },
        }}
      >
        <Autocomplete
          fullWidth
          options={addressSuggestions}
          filterOptions={(options) => options}
          getOptionLabel={(v) => formatAddress(v as EnteredAddress)}
          value={null}
          disableClearable={!!selectedValue}
          forcePopupIcon={false}
          onChange={(e, v) => handleChange(e, v as EnteredAddress | null)}
          onInputChange={handleInputChange}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField
              {...params}
              placeholder='Search Address'
              slotProps={{
                input: {
                  ...params.InputProps,
                  startAdornment: (
                    <Search width='14px' height='14px' color='text.subdued' style={{ marginRight: '10px' }} />
                  ),
                },
              }}
            />
          )}
          renderOption={(props, option: EnteredAddress) => (
            <li {...props} key={props.key}>
              <TypographyHighlighter
                text={formatAddress(option)}
                variant='bodySmall'
                color='common.black'
                highlightText={inputValue}
                highlightStyle={{
                  fontWeight: 700,
                  backgroundColor: 'transparent',
                }}
              />
            </li>
          )}
          sx={{
            '@media(max-width: 899px)': { maxWidth: '380px' },
          }}
        />
      </Grid>
    </Grid>
  );
};
